// ================================
// Overlay Layers (Rights Driven)
// ================================

import Group from 'ol/layer/Group';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';

import { hasRight } from './rightsUtil.js';

// --------------------------------
// Layer Factory Functions
// --------------------------------
function createOSMStandard() {
  return new TileLayer({
    title: 'OSM Standard',
    type: 'base',
    visible: true,
    tooltip: 'Default OpenStreetMap style – general purpose map for everyday use.',
    source: new OSM({
      url: 'https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    }),
  });
}

function createOSMHumanitarian() {
  return new TileLayer({
    title: 'OSM Humanitarian',
    type: 'base',
    visible: false,
    tooltip: 'Humanitarian OSM – emphasizes roads and infrastructure.',
    source: new OSM({
      url: 'https://{a-c}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
    }),
  });
}

function createOSMTopo() {
  return new TileLayer({
    title: 'OSM Topographic',
    type: 'base',
    visible: false,
    tooltip: 'Topographic map with contours and terrain.',
    source: new OSM({
      url: 'https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png',
    }),
  });
}

function createCartoLight() {
  return new TileLayer({
    title: 'Carto Light',
    type: 'base',
    visible: false,
    tooltip: 'Minimal light map – good for overlays.',
    source: new XYZ({
      url: 'https://{a-d}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      attributions:
        '&copy; OSM contributors &copy; CARTO',
    }),
  });
}

function createCartoDark() {
  return new TileLayer({
    title: 'Carto Dark',
    type: 'base',
    visible: false,
    tooltip: 'Dark themed map – ideal for night mode.',
    source: new XYZ({
      url: 'https://{a-d}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
      attributions:
        '&copy; OSM contributors &copy; CARTO',
    }),
  });
}

function createCyclOSM() {
  return new TileLayer({
    title: 'CyclOSM',
    type: 'base',
    visible: false,
    tooltip: 'Cycling-focused map.',
    source: new OSM({
      url: 'https://{a-c}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
    }),
  });
}

function createEsriSatellite() {
  return new TileLayer({
    title: 'E-Satellite',
    type: 'base',
    visible: false,
    tooltip: 'High-resolution satellite imagery.',
    source: new XYZ({
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attributions: 'Tiles © Esri',
    }),
  });
}

// --------------------------------
// Layer Configuration (RIGHTS MAP)
// --------------------------------
const layerConfigs = [
  {
    right: "bl.osm_sta",
    create: createOSMStandard
  },
  {
    right: "bl.osm_humanitarian",
    create: createOSMHumanitarian
  },
  {
    right: "bl.osm_top",
    create: createOSMTopo
  },
  {
    right: "bl.carto_light",
    create: createCartoLight
  },
  {
    right: "bl.carto_dark",
    create: createCartoDark
  },
  {
    right: "bl.cyclosm",
    create: createCyclOSM
  },
  {
    right: "bl.satalite",
    create: createEsriSatellite
  }
];

// --------------------------------
// Create Overlay Group
// --------------------------------
export function createOverlayGroup() {

  const layers = [];

  layerConfigs.forEach(cfg => {

    const layer = cfg.create();

    const hasLayerRight =
      !cfg.right || hasRight(cfg.right);

    // Unauthorized → force OFF
    if (!hasLayerRight) {

      layer.setVisible(false);
      layer.set('disabledByRights', true);

      //console.log("Overlay disabled by rights:", layer.get('title'));

    } else {

      layer.set('disabledByRights', false);
    }

    layers.push(layer);
  });

  // Ensure one allowed layer visible
  const allowedVisible = layers.some(layer =>
    layer.getVisible() &&
    !layer.get('disabledByRights')
  );

  if (!allowedVisible) {

    const firstAllowed = layers.find(layer =>
      !layer.get('disabledByRights')
    );

    if (firstAllowed) {
      firstAllowed.setVisible(true);
    }
  }

  return new Group({
    title: 'Overlay',
    zIndex: 2,
    layers: layers,
  });
}
